/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Heading } from '@chakra-ui/react';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import React from 'react';
import { FaPlus } from 'react-icons/fa6';

import { cellFactory } from '@/components/available-preview-list/aplUtils';
import styles from '@/components/view-editor/styles.module.scss';
import ViewLayoutSortableItem from '@/components/view-editor/ViewLayoutSortableItem';
import VirtualTable from '@/components/virtual-table/VirtualTable';
import { EMPTY_ARRAY_LENGTH, NUMERICAL_ONE, NUMERICAL_ZERO } from '@/constants/defaults';
import { SORTING_ORDER } from '@/constants/ui';
import useCustomColumnConfig from '@/hooks/useCustomColumnConfig';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setViewEditorDraftMultiColumnSort } from '@/store/slices/viewEditor.slice';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';
import { UniqueId } from '@/types/ui.types';
import { MultiSortColumn, ViewLayoutListColumns } from '@/types/view.types';

const getHeader = () => (
  <Heading size={'md'} marginBottom={'10px'} fontWeight={'medium'}>
    Advanced Sorting Rules
  </Heading>
);

const AdvancedSorting = (): React.JSX.Element => {
  const { viewDraft: view } = useAppSelector((state) => state.viewEditor);

  const { theme } = view;

  const dispatch = useAppDispatch();

  const { useCustomColumnDisplayName, customColumnDisplayNames } = useCustomColumnConfig();
  const [custom1Name, custom2Name, custom3Name, custom4Name] = customColumnDisplayNames;

  const customDisplayNameMap = React.useMemo<Partial<Record<ViewLayoutListColumns, string>>>(() => {
    if (!useCustomColumnDisplayName) return {};

    return {
      ...(custom1Name && { [ViewLayoutListColumns.CUSTOM_1]: custom1Name }),
      ...(custom2Name && { [ViewLayoutListColumns.CUSTOM_2]: custom2Name }),
      ...(custom3Name && { [ViewLayoutListColumns.CUSTOM_3]: custom3Name }),
      ...(custom4Name && { [ViewLayoutListColumns.CUSTOM_4]: custom4Name }),
    };
  }, [useCustomColumnDisplayName, custom1Name, custom2Name, custom3Name, custom4Name]);

  // we only have the selected columns name in the store,
  // so we use that info to create a more 'structured' object to identify an option
  const convertToSelectOptions = (columns: ViewLayoutListColumns[]) => {
    return columns.map((column) => ({
      columnValue: column,
      id: Object.values(ViewLayoutListColumns).indexOf(column) + NUMERICAL_ONE,
      key: column,
      sortDirection: SORTING_ORDER.ASC,
    }));
  };

  const selectedPreviewColumns = React.useMemo(
    () => (theme.data.listColumns ? convertToSelectOptions(theme.data.listColumns) : []),
    [theme.data.listColumns],
  );
  const selectedColumnsLength = selectedPreviewColumns.length;

  const [firstRule] = selectedPreviewColumns;
  const initialRulesList = theme.data.multiSortColumns?.length ? theme.data.multiSortColumns : [firstRule];

  const [rulesList, setRulesList] = React.useState(initialRulesList);

  const getInitialSortByOptions = React.useMemo(
    () => selectedPreviewColumns.filter((option) => rulesList.every((rule) => rule.columnValue !== option.columnValue)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [canAddMoreRules, setCanAddMoreRules] = React.useState(true);
  const [sortByOptions, setSortByOptions] = React.useState(getInitialSortByOptions);
  const [selectedItemIds, setSelectedItemIds] = React.useState<UniqueId[]>([]);

  React.useEffect(() => {
    dispatch(setViewEditorDraftMultiColumnSort(rulesList));
    if (selectedColumnsLength === rulesList.length) {
      setCanAddMoreRules(false);
    }
  }, [dispatch, rulesList, selectedColumnsLength]);

  const getUnselectedOptions = React.useCallback(
    (newRules: MultiSortColumn[]) =>
      selectedPreviewColumns.filter((option) => newRules.every((rule) => rule.columnValue !== option.columnValue)),
    [selectedPreviewColumns],
  );

  const updateSortByOptions = (newRules: MultiSortColumn[]) => {
    setSortByOptions(getUnselectedOptions(newRules));
  };

  const handleDelete = (value: ViewLayoutListColumns) => {
    const filteredRules = rulesList.filter((rule: MultiSortColumn) => rule.columnValue !== value);
    setRulesList(filteredRules);
    setCanAddMoreRules(true);
    updateSortByOptions(filteredRules);
  };

  const handleOrderChange = (updatedRule: MultiSortColumn) => {
    const updatedRules = rulesList.map((rule) => {
      return rule.columnValue === updatedRule.columnValue ? updatedRule : rule;
    });
    setRulesList(updatedRules);
  };

  const handleUpdate = (updatedRule: MultiSortColumn, oldRuleValue: ViewLayoutListColumns) => {
    const index = rulesList.findIndex((r) => r.columnValue === oldRuleValue);
    const updatedRules = rulesList.map((rule, i) => {
      return i === index ? updatedRule : rule;
    });
    setRulesList(updatedRules);
    updateSortByOptions(updatedRules);
  };

  const addEmptyRule = () => {
    const unSelectedOptions = getUnselectedOptions(rulesList);
    // there are still unused options in the selected columns
    if (unSelectedOptions.length > EMPTY_ARRAY_LENGTH) {
      const newRule = {
        ...unSelectedOptions[NUMERICAL_ZERO],
        sortDirection: SORTING_ORDER.ASC,
      };
      const newRules = [...rulesList, newRule];
      setRulesList(newRules);
      // disable adding more after adding the last one
      if (selectedColumnsLength === newRules.length) {
        setCanAddMoreRules(false);
        setSortByOptions([]);
      } else {
        updateSortByOptions(newRules);
      }
    }
  };

  const handleUpdateOrder = (data: unknown[]) => {
    setRulesList(data as MultiSortColumn[]);
  };

  const handleItemClick = (_e:React.MouseEvent, itemId: UniqueId) => {
    if(selectedItemIds.includes(itemId)){
      setSelectedItemIds(selectedItemIds.filter((id)=> id !== itemId))
    } else {
      setSelectedItemIds([...selectedItemIds, itemId]);
    }
  };

  const disableDelete = rulesList.length === NUMERICAL_ONE;

  const cellContentInjector = (info: CellContext<MultiSortColumn, unknown>) => {
    return (
      <ViewLayoutSortableItem
        customDisplayNameMap={customDisplayNameMap}
        disableDelete={disableDelete}
        handleDelete={handleDelete}
        handleOrderChange={handleOrderChange}
        handleUpdate={handleUpdate}
        index={info.row.index}
        rule={info.row.original}
        sortByOptions={sortByOptions}
      />
    );
  };

  const createCell = (info: CellContext<MultiSortColumn, ViewLayoutListColumns>) =>
    cellFactory<MultiSortColumn>({
      cellContentInjector,
      cellContext: info,
      clickHandler: handleItemClick,
      isSelected: selectedItemIds.includes(info.row.original.id),
      text: '',
    });

  const columnHelper = createColumnHelper<MultiSortColumn>();

  const columns = [
    columnHelper.accessor('columnValue', {
      cell: createCell,
      header: getHeader,
      id: 'columnValue',
    }),
  ];

  return (
    <Box width={'100%'}>
      <VirtualTable
        columns={columns}
        data={rulesList}
        handleUpdateOrder={handleUpdateOrder}
        overrideHeader
        overrideTableSorting
        maxTableWidth={'1200px'}
        tableRowHeight={82}
        tableWidth={'800px'}
        uniqueSelectedIds={new Set(selectedItemIds)}
        showColorPicker={false}
        showSideControls={true}
        uniqueRowKey={'id'}
      />
      <Button
        data-testid={ELEMENT_DATA_TEST_IDS.ADD_RULE_BUTTON}
        isDisabled={!canAddMoreRules}
        leftIcon={<FaPlus />}
        onClick={addEmptyRule}
        className={classNames(styles.addRuleButton)}
      >
        Add another sort column
      </Button>
    </Box>
  );
};

export default AdvancedSorting;
